import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  browserSessionPersistence,
  browserPopupRedirectResolver,
  setPersistence,
  browserLocalPersistence, // For Persistence.LOCAL
  signInWithPopup,
  OAuthProvider,          // For Apple Sign in
  GoogleAuthProvider,     // For FirebaseUI Google Sign in
  EmailAuthProvider,       // For FirebaseUI Email Sign in
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  // Used by the custom action handler at /password-reset/confirm, which lets
  // users set a new password on hiiker.app instead of the Firebase-hosted page.
  verifyPasswordResetCode,
  confirmPasswordReset
} from 'firebase/auth';
import type { Auth } from 'firebase/auth';
import { app } from './initialise.js'; // Import the initialized app
import { lazyBrowserPopupRedirectResolver, warmUpPopupRedirectResolver } from './popupRedirectResolver';

// Create the Auth instance the same way getAuth() does (same persistence list,
// so an existing signed-in session is found and migrated exactly as before),
// except the popup/redirect resolver doesn't build its hidden iframe at start-up
// on mobile/Safari. See popupRedirectResolver.ts. Sign-in UI calls
// warmUpAuthPopups() when it opens.
function createAuth(): Auth {
  try {
    return initializeAuth(app, {
      persistence: [
        indexedDBLocalPersistence,
        browserLocalPersistence,
        browserSessionPersistence,
      ],
      popupRedirectResolver: lazyBrowserPopupRedirectResolver(browserPopupRedirectResolver),
    });
  } catch (error) {
    // Something already called getAuth() for this app (initializeAuth throws
    // auth/already-initialized). Reuse that instance; it just keeps the SDK's
    // default proactive iframe.
    console.warn('[FirebaseAuth] Auth already initialised; reusing it:', error);
    return getAuth(app);
  }
}

// Get the Auth service instance, linked to the initialized app
const auth = createAuth();

/**
 * Start building Firebase's popup sign-in iframe ahead of a likely
 * signInWithPopup(): call it when sign-in UI opens. Idempotent; never rejects.
 */
function warmUpAuthPopups(): Promise<void> {
  return warmUpPopupRedirectResolver(auth);
}

// Set persistence (equivalent to firebase.auth.Auth.Persistence.LOCAL)
// This should ideally be called early, perhaps even here or right after auth init.
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    console.log("Firebase Auth persistence set to local.");
  })
  .catch((error) => {
    console.error("Error setting Firebase Auth persistence:", error);
  });

// --- Sign-in Methods ---

// Apple Sign-in (replaces firebase.hiikerSignInWithApple)
async function signInWithApple(signInSuccessUrl = '/profile') {
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');

  try {
    const userCredential = await signInWithPopup(auth, provider); // Use signInWithPopup from v9 SDK
    const idToken = await userCredential.user.getIdToken();
    const redirectUrl = `/firebase/auth?accessToken=${idToken}`; // Your backend endpoint
    window.location.assign(redirectUrl);
  } catch (error) {
    alert(error)
  }
}

// Export the auth instance and specific methods/providers needed elsewhere
export {
  auth,
  warmUpAuthPopups,
  signInWithApple,
  // Export providers needed by FirebaseUI
  GoogleAuthProvider,
  EmailAuthProvider,
  onAuthStateChanged,
  signOut,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  verifyPasswordResetCode,
  confirmPasswordReset
};
